import get from 'lodash/get'
import map from 'lodash/map'
import every from 'lodash/every'
import each from 'lodash/each'
import filter from 'lodash/filter'
import find from 'lodash/find'

const isFormLike = (datum, key) =>
  key.substr(0, 1) !== '_' &&
  datum &&
  typeof datum === 'object' &&
  Object.prototype.hasOwnProperty.call(datum, 'valid') &&
  Object.prototype.hasOwnProperty.call(datum, 'value')
    ? key
    : null

/**
 * Finds the `{ value, valid }` fields a component declares, which is how a form
 * gets validated without listing every field by hand.
 *
 * Reads `$data` and `$props` rather than the instance. Vue 3's instance proxy
 * deliberately returns NO keys from its `ownKeys` trap in production — it warns
 * about exactly this in development — so enumerating `this` found the fields
 * while developing and nothing at all in a production build.
 *
 * That failed open rather than closed. With no keys, `_validate()` produced an
 * empty result set, so `Object.keys(this.$validations)` was empty, so
 * `$valid({ topics: [] })` reduced to `!every([])` — true. Every form that
 * relies on implicit discovery would have reported itself valid in production
 * without checking a single field, the checkout form among them.
 */
const formLikeKeys = function (vm) {
  return [...filter(map(vm.$data || {}, isFormLike)), ...filter(map(vm.$props || {}, isFormLike))]
}

export default {
  // No module-level "installed" guard: Vue 3 installs plugins per app and
  // `app.use()` already ignores a repeat, so a global flag left every app after
  // the first without validations.
  install(Vue) {
    Vue.mixin({

      computed: {
        // Was `Object.defineProperty(Vue.config.globalProperties, '$validations', { get })`.
        // Vue 3 reads a global property with a plain `globalProperties[key]`
        // access (runtime-core PublicInstanceProxyHandlers), so the receiver is
        // the globalProperties object rather than the component — `this.$data`
        // was undefined and every read of `this.$validations` threw, including
        // the one in PaymentForm's button handler.
        //
        // As a computed on the mixin, `this` is the instance again.
        $validations() {
          return get(this, ['$data', '$validations', 'results'], {})
        }
      },
      created() {
        if (this.$options.validation) {
          this.$data.$validations = {
            keys: [...formLikeKeys(this), ...Object.keys(get(this.$options, ['validation', 'topics'], {}))],
            results: [],
            triggered: {}
          }

          this.$validate()
        }
      },

      // `validation: { refs: { someRef: 'some-event' } }` used to subscribe to a
      // child through `comp.$on(...)` from `mounted`/`updated`. Vue 3 removed the
      // instance event bus, so that call threw and took the whole mounted hook
      // with it — which is what broke the checkout form.
      //
      // Rewiring it was not the answer, because the feature earned nothing:
      //
      //   - PaymentForm, the only component where it actually fired, already
      //     binds `@button-clicked="handleButtonClicked"` in its template, and
      //     that handler already calls `$validate({ throughTrigger: 'payment' })`.
      //     The subscription just did the same work twice.
      //   - ModalChangeDetails declares `refs: { updateDetails: 'click' }` but has
      //     no `ref="updateDetails"` anywhere in its template, so the `comp`
      //     guard skipped it. Dead config.
      //
      // The only other thing the method did was seed `triggered[ref] = false`,
      // and `$invalid` reads that key for truthiness alone — an absent key is
      // falsy in exactly the same way. So the behaviour is unchanged.
      //
      // A child that needs to trigger validation should bind the event in the
      // template, the way PaymentForm already does.
    })



    Vue.config.globalProperties.$valid = function ({ after: ref, topics }) {
      return !this.$invalid({ after: ref, topics })
    }

    Vue.config.globalProperties.$invalid = function ({ after: refOrRefs, topics }) {
      let val = false
      const hasRef = !refOrRefs

      const anyTriggered = () => {
        const refs = Array.isArray(refOrRefs) ? refOrRefs : [refOrRefs]

        return !!find(refs, (ref) => this.$data.$validations.triggered[ref])
      }

      if (hasRef) {
        // Calculate immediately because this does not depend on a ref
        const validations = this._validate()

        val = !every(map(topics, (t) => get(validations, [t, 'valid'], false)))
      } else if (anyTriggered()) {
        // Use the cached validations in this.$validations
        try {
          val = !every(map(topics, (t) => this.$validations[t].valid))
        } catch (e) {
          // `Vue.util` is a Vue 2 constructor property. `app.use()` hands this
          // plugin the app instance, which has no `util`, so the old call threw
          // a TypeError out of the handler and replaced a warning with a crash.
          console.warn(`Could not find a validation value for one or more topics ${topics}.`)
        }
      }

      return val
    }

    Vue.config.globalProperties._validate = function () {
      // Early call to validate before $data has been created
      const normalize = function (val) {
        let normalized = { valid: false, value: '' }

        if (typeof val === 'boolean') {
          normalized.valid = val
        }

        if (typeof val === 'object') {
          normalized = Object.assign({}, normalized, val)
        }

        return normalized
      }

      const results = {}

      each(get(this, ['$data', '$validations', 'keys'], []), (key) => {
        let val = null

        try {
          val = this[key] ? this[key] : this.$options.validation.topics[key].call(this)
        } catch (e) {
          console.warn(`Could not find ${key} in the component validation.topics, check your component`)
        }

        results[key] = normalize(val)
      })

      return results
    }

    Vue.config.globalProperties.$validate = function (options = {}) {
      this.$data.$validations.results = this._validate()

      if (options.throughTrigger) {
        this.$data.$validations.triggered[options.throughTrigger] = true
        this.$forceUpdate()
      }
    }
  }
}
